import React, { useMemo } from 'react';
import { LineChart, Line, ReferenceLine, ResponsiveContainer, Dot, YAxis } from 'recharts';
import { KpiMeta } from '../config/kpi-config';
import { getTokens } from '../styles/tokens';

interface EnhancedSparklineProps {
  timeseries: Array<{ year: number; value: number; prevYearValue: number | null }>;
  meta: KpiMeta;
}

// Helper function to normalize data to index-based values (first year = 0)
const toMicroSeries = (data: Array<{ year: number; value: number }>, type: string, mode: 'index' = 'index') => {
  if (!data || data.length === 0) return [];
  
  const firstValue = data[0]?.value;
  if (!firstValue || firstValue === 0) return [];
  
  return data.map((point, index) => ({
    x: index,
    y: mode === 'index' ? ((point.value / firstValue) - 1) * 100 : point.value,
    year: point.year,
    value: point.value
  }));
};

const EnhancedSparkline: React.FC<EnhancedSparklineProps> = ({ timeseries, meta }) => {
  // Normalize the timeseries to indexed values (first year = 0, change in %)
  const indexedData = useMemo(() => {
    return toMicroSeries(timeseries, meta.type, 'index');
  }, [timeseries, meta.type]);

  // Compute a dynamic Y domain to amplify small movements
  const yDomain = useMemo(() => {
    if (!indexedData.length) return [-10, 10] as [number, number];
    const ys = indexedData.map(d => Number(d.y)).filter(v => Number.isFinite(v));
    if (!ys.length) return [-10, 10] as [number, number];
    let min = Math.min(...ys);
    let max = Math.max(...ys);
    const range = max - min;
    if (range < 6) {
      // If variation is small, expand around the midpoint to make movement visible
      const mid = (max + min) / 2;
      min = mid - 3;
      max = mid + 3;
    } else {
      // Add a little padding
      min -= 1.5;
      max += 1.5;
    }
    // Clamp to a reasonable window
    min = Math.max(-30, min);
    max = Math.min(30, max);
    return [min, max] as [number, number];
  }, [indexedData]);

  // Determine trend color based on overall direction using standardized tokens
  const trendColor = useMemo(() => {
    const tokens = getTokens(); // Default to light theme, could be enhanced with theme detection
    
    if (indexedData.length < 2) return tokens.neutral;
    
    const lastValue = indexedData[indexedData.length - 1]?.y;
    if (lastValue === undefined || lastValue === null) return tokens.neutral;
    
    // Consider the direction of change relative to whether higher is better
    if (Math.abs(lastValue) < 2) return tokens.neutral; // Neutral for small changes
    
    if (meta.higherIsBetter === null) return tokens.neutral; // Neutral for neutral metrics
    
    if (lastValue > 0) {
      return meta.higherIsBetter ? tokens.positive : tokens.negative;
    } else {
      return meta.higherIsBetter ? tokens.negative : tokens.positive;
    }
  }, [indexedData, meta.higherIsBetter]);

  // Get the latest change for display
  const latestChange = indexedData.length > 0 ? indexedData[indexedData.length - 1]?.y : 0;

  if (indexedData.length === 0) {
    return (
      <div className="w-full h-9 flex items-center justify-center text-xs text-slate-400">
        Ingen data
      </div>
    );
  }

  return (
    <div className="flex items-center gap-2">
      <ResponsiveContainer width="100%" height={40}>
        <LineChart data={indexedData} margin={{ top: 2, bottom: 2, left: 2, right: 2 }}>
          {/* Dynamic Y domain to increase perceived movement */}
          <YAxis domain={yDomain} hide />
          
          {/* Reference line at 0 (no change) */}
          <ReferenceLine 
            y={0} 
            stroke="hsl(var(--border))" 
            strokeDasharray="2 4" 
            strokeOpacity={0.5}
          />
          
          {/* Main trend line */}
          <Line
            type="monotone"
            dataKey="y"
            stroke={trendColor}
            strokeWidth={2}
            dot={false}
            connectNulls={true}
          />
          
          {/* Dot at the last point */}
          <Dot 
            r={3} 
            fill={trendColor}
            stroke={trendColor}
            strokeWidth={1}
          />
        </LineChart>
      </ResponsiveContainer>
      
      {/* Direct label showing the latest change */}
      <div className="text-xs font-medium" style={{ color: trendColor }}>
        {latestChange > 0 ? '+' : ''}{latestChange.toFixed(1)}%
      </div>
    </div>
  );
};

export default EnhancedSparkline;
